import { useRef } from 'react';
import { Search, Loader2, XIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Props = {
  placeholder: string;
  // Must update synchronously with onChange (e.g. local state) — binding this to debounced/derived state breaks typing.
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  isLoading: boolean;
  className?: string;
};

const InputSearch = ({ placeholder, value, onChange, onClear, isLoading = false, className }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const Icon = isLoading ? Loader2 : Search;
  const displayValue = value ?? inputRef.current?.value;

  const handleClear = () => {
    if (value === undefined && inputRef.current) {
      inputRef.current.value = '';
    }
    onClear?.();
  };

  return (
    <div className={cn('relative', className)}>
      <Icon
        className={cn(
          'absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4',
          isLoading && 'animate-spin',
        )}
      />
      <Input placeholder={placeholder} className="pl-8" value={value} onChange={onChange} ref={inputRef} />
      {displayValue && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
          aria-label="Clear search"
          onClick={handleClear}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export { InputSearch };
